"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { useCart } from "@/components/cart/cart-context";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import { useRouter } from "next/navigation";
import { getNiceErrorMessage } from "@/lib/httpError";
import {
  type FooEvents,
  type FooEventsSlots,
  isUpcoming,
  getEventDateLabel,
  getEventKind,
  getEventDayRows,
} from "@/lib/eventDateHelpers";
import { apiPath } from "@/lib/apiPath";

type Props = {
  data: unknown;
  lang?: string;
};

type WPTitle = { rendered?: string };
type WPContent = { rendered?: string };

type FooEventsRaw = FooEvents & {
  WooCommerceEventsTicketLogo?: string;
  WooCommerceEventsEventDetailsText?: string;
};

type YoastHeadJson = {
  og_image?: Array<{ url?: string; width?: number; height?: number }>;
};

type WPPageRef = {
  post_title?: string;
  slug?: string;
};

type ProductACF = {
  ticket_required?: boolean;
  booking_link?: string | null;
};

type ProductData = {
  id?: number;
  link?: string;
  title?: WPTitle;
  content?: WPContent;
  excerpt?: WPContent;
  featured_image?: string;
  yoast_head_json?: YoastHeadJson;
  fooevents?: FooEvents;
  fooevents_raw?: FooEventsRaw;
  fooevents_slots?: FooEventsSlots | null;
  acf?: ProductACF;
  class_list?: Record<string, string>;
  tickets_left?: number | null;
  price_amount?: number | null;
  is_free?: boolean;
  sold_individually?: boolean;
};

type ProductLabels = {
  no_ticket_required?: string;
  no_reservation_note?: string;
  free_or_ticket_note?: string;
  past_note?: string;
  free?: string;
  ended_label?: string;
  out_of_stock_label?: string;
  included_free?: string;
  quantity_label?: string;
  available_notice?: string;
  slot_placeholder?: string;
  slot_full_note?: string;
  reserve_now?: string;
  reserve_seat?: string;
  processing?: string;
  error_generic?: string;
  event_date_label?: string;
  event_time_label?: string;
  event_location_label?: string;
  description_heading?: string;
  event_details_heading?: string;
  sold_individually_note?: string;
  checking_availability?: string;
  unlimited_label?: string;
  available_singular?: string;
  available_plural?: string;
  tickets_remaining_singular?: string;
  tickets_remaining_plural?: string;
  already_in_cart?: string;
  description_coming_soon?: string;
  details_not_available?: string;
  event_info_coming_soon?: string;
  select_at_least_one?: string;
  missing_product_id?: string;
  missing_basket_route?: string;
  add_to_cart_failed?: string;
  decrease_qty_aria?: string;
  increase_qty_aria?: string;
  decrease_slot_qty_aria?: string;
  increase_slot_qty_aria?: string;
  schedules_label?: string;
};

type GlobalText = {
  products?: ProductLabels;
};

type LayoutData = {
  templateRoutes?: Record<string, string | undefined>;
  company_data?: {
    home_page?: WPPageRef;
    shop_page?: WPPageRef;
  };
  global_text?: GlobalText;
};

type TabKey = "description" | "details";

type CartItem = {
  id?: number | string;
  key?: string;
  quantity?: number | string;
  item_data?: Array<{ key?: string; value?: unknown }>;
  meta_data?: Array<{ key?: string; value?: unknown }>;
  extensions?: Record<string, unknown>;
};

type CartResponse = {
  items?: CartItem[];
};

// ES site: a booking link always wins (external button, no Woo ticket flow).
// Without a link, paid events show price only and ticket_required applies to free events.
// PT is unchanged.
const isEsSite = process.env.NEXT_PUBLIC_DEFAULT_LANG === "es";

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object";
}

function isProductData(v: unknown): v is ProductData {
  return isObject(v);
}

function toSafeInt(v: unknown, fallback: number) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.floor(n);
}

function clampInt(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, Math.floor(n)));
}

function resolveLocale(lang: string): string {
  if (lang === "pt") return "pt-PT";
  if (lang === "es") return "es-ES";
  return lang || "pt-PT";
}

function formatTime(hour?: unknown, minutes?: unknown) {
  const h = toSafeInt(hour, Number.NaN);
  const m = toSafeInt(minutes, Number.NaN);

  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;

  const mm = String(m).padStart(2, "0");
  return `${String(h).padStart(2, "0")}:${mm}`;
}

function getRenderedTitle(entry: ProductData, fallback: string) {
  return entry.title?.rendered || fallback;
}

function getDescriptionHtml(entry: ProductData) {
  return entry.excerpt?.rendered || entry.content?.rendered || "";
}

function getEventDetailsHtml(entry: ProductData) {
  return entry.fooevents_raw?.WooCommerceEventsEventDetailsText || entry.content?.rendered || "";
}

function getExternalBookingUrl(acf?: ProductACF): string | null {
  const trimmed = (acf?.booking_link ?? "").trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

function pickHeroImage(entry: ProductData, titleFallback: string) {
  const fromFeatured = entry.featured_image;
  const fromTicketLogo = entry.fooevents_raw?.WooCommerceEventsTicketLogo;
  const fromYoast = entry.yoast_head_json?.og_image?.[0]?.url;
  const srcRaw = fromFeatured || fromTicketLogo || fromYoast || "";
  const src = srcRaw ? toAbsoluteUrl(srcRaw) ?? srcRaw : "";
  return { src, alt: getRenderedTitle(entry, titleFallback) };
}

function safePageLabel(page: WPPageRef | undefined, fallback: string) {
  const t = (page?.post_title ?? "").trim();
  return t || fallback;
}

function safePageHref(page?: WPPageRef) {
  const slug = (page?.slug ?? "").trim();
  return slug || "/";
}

function safeStr(v: unknown) {
  return typeof v === "string" ? v : "";
}

function formatTimeString24(v: unknown): string {
  const raw = safeStr(v).trim();
  if (!raw) return "";

  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return raw.replace(/\s*(AM|PM)\s*$/i, "").trim();

  let h = Number(match[1]);
  const m = Number(match[2] ?? "0");
  const meridiem = match[3]?.toUpperCase();

  if (!Number.isFinite(h) || !Number.isFinite(m)) return raw;

  if (meridiem === "PM" && h < 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;

  if (h < 0 || h > 23 || m < 0 || m > 59) return raw;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function safeSlotStock(stock: unknown): number | null {
  if (stock === null || stock === undefined) return null;
  const n = Number(stock);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
}

function parseDateOnlyLocal(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatSlotDateLabel(dateStr?: string | null, locale?: string) {
  const d = parseDateOnlyLocal(dateStr);
  if (!d) return dateStr || "";
  try {
    return new Intl.DateTimeFormat(locale || "", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return dateStr || "";
  }
}

function getUniqueSortedSlotDates(
  slots: Array<NonNullable<FooEventsSlots["slots"]>[number]>
): string[] {
  return Array.from(new Set(slots.map((s) => safeStr(s.date)).filter(Boolean))).sort((a, b) => {
    const da = parseDateOnlyLocal(a);
    const db = parseDateOnlyLocal(b);
    if (!da && !db) return a.localeCompare(b);
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
}

function groupSlotsByDate(
  slots: Array<NonNullable<FooEventsSlots["slots"]>[number]>,
  dateFallback: string
): Array<{ dateLabel: string; dateKey: string; items: typeof slots }> {
  const map = new Map<string, typeof slots>();
  for (const s of slots) {
    const dateKey = safeStr(s.date) || dateFallback;
    const arr = map.get(dateKey) ?? [];
    arr.push(s);
    map.set(dateKey, arr);
  }
  const keys = Array.from(map.keys()).sort((a, b) => {
    const da = parseDateOnlyLocal(a);
    const db = parseDateOnlyLocal(b);
    if (!da && !db) return a.localeCompare(b);
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
  return keys.map((k) => ({
    dateKey: k,
    dateLabel: k,
    items: (map.get(k) ?? []).slice().sort((a, b) => safeStr(a.time).localeCompare(safeStr(b.time))),
  }));
}

function getCartItemSlotIdentity(it: CartItem): { slotId: string; bookingDateId: string } | null {
  const ext = it.extensions;
  if (ext && typeof ext === "object") {
    const bookings = (ext as Record<string, unknown>)["bookings"];
    if (bookings && typeof bookings === "object") {
      const b = bookings as Record<string, unknown>;
      const slotId = safeStr(b["slot_id"]);
      const bookingDateId = safeStr(b["booking_date_id"]);
      if (slotId && bookingDateId) return { slotId, bookingDateId };
    }
  }
  const meta = Array.isArray(it.meta_data) ? it.meta_data : [];
  const readMeta = (wanted: string) => {
    const found = meta.find((m) => safeStr(m?.key).toLowerCase() === wanted.toLowerCase());
    const value = found?.value;
    if (typeof value === "string") return value.trim();
    if (typeof value === "number") return String(value);
    return "";
  };
  const slotId =
    readMeta("WooCommerceEventsBookingSlotID") ||
    readMeta("_WooCommerceEventsBookingSlotID") ||
    readMeta("fooevents_slot_id");
  const bookingDateId =
    readMeta("WooCommerceEventsBookingDateID") ||
    readMeta("_WooCommerceEventsBookingDateID") ||
    readMeta("fooevents_booking_date_id");
  if (slotId && bookingDateId) return { slotId, bookingDateId };
  return null;
}

function getQtyInCartForSlot(
  cartItems: CartItem[],
  slotId: string,
  bookingDateId: string
): number {
  let sum = 0;
  for (const item of cartItems) {
    const identity = getCartItemSlotIdentity(item);
    if (!identity) continue;
    if (identity.slotId === slotId && identity.bookingDateId === bookingDateId) {
      sum += toSafeInt(item.quantity, 0);
    }
  }
  return Math.max(0, sum);
}

function hasAvailableNonSlotTickets(entry: ProductData): boolean {
  if (typeof entry.tickets_left === "number" && Number.isFinite(entry.tickets_left)) {
    return entry.tickets_left > 0;
  }
  return true;
}

function hasAvailableSlots(
  slots?: FooEventsSlots["slots"] | null,
  remainingById?: Record<string, number | null>
): boolean {
  if (!slots || slots.length === 0) return false;
  return slots.some((s) => {
    const id = safeStr(s.id);
    if (remainingById && id && Object.prototype.hasOwnProperty.call(remainingById, id)) {
      const remaining = remainingById[id];
      return remaining === null || remaining > 0;
    }
    const stock = safeSlotStock(s.stock);
    return stock === null || stock > 0;
  });
}

function getStatusLabel(
  entry: ProductData,
  labels: { free: string; endedLabel: string; outOfStockLabel: string },
  remainingById?: Record<string, number | null>,
  isPast?: boolean
) {
  if (isPast) return labels.endedLabel;
  const eventType = entry.fooevents?.WooCommerceEventsType ?? null;
  const slots = entry.fooevents_slots?.slots ?? [];
  const hasSlots = Array.isArray(slots) && slots.length > 0 && eventType !== "single";
  const available = hasSlots
    ? hasAvailableSlots(slots, remainingById)
    : hasAvailableNonSlotTickets(entry);
  if (!available) return labels.outOfStockLabel;
  if (entry.is_free === true) return labels.free;
  const amount = typeof entry.price_amount === "number" ? entry.price_amount : NaN;
  if (!isNaN(amount) && amount > 0) {
    try {
      return new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `€${amount}`;
    }
  }
  return labels.free;
}

function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`
  );
}

function renderPastNote(
  template: string,
  shopHref: string,
  shopLabel: string
): React.ReactNode {
  const parts = template.split("{shop}");
  if (parts.length === 1) {
    return (
      <>
        {template}{" "}
        <Link className="underline" href={shopHref}>{shopLabel}</Link>.
      </>
    );
  }
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 ? (
        <Link className="underline" href={shopHref}>{shopLabel}</Link>
      ) : null}
    </React.Fragment>
  ));
}

export default function ProductPageClient({ data, lang = "" }: Props) {
  const { setItemsCount, refreshCart } = useCart();

  const layoutUnknown = useLayoutData();
  const layout = (layoutUnknown ?? {}) as LayoutData;
  const routes = layout.templateRoutes ?? {};
  const router = useRouter();
  const basketHref = routes["page-basket.php"] || null;

  const productLabels = layout.global_text?.products ?? {};
  const L = {
    productFallback: "Product",
    homeFallback: "Home",
    dateFallback: "Date",
    detailsTab: productLabels.event_details_heading ?? "Event details",
    noTicketRequired: productLabels.no_ticket_required ?? "No ticket required",
    noReservationNote: productLabels.no_reservation_note ?? "You can attend without a reservation.",
    pastNote: productLabels.past_note ?? "See upcoming events at {shop}.",
    free: productLabels.free ?? "Free",
    endedLabel: productLabels.ended_label ?? "Ended",
    outOfStockLabel: productLabels.out_of_stock_label ?? "Sold out",
    quantityLabel: productLabels.quantity_label ?? "Tickets selected",
    availableNotice: productLabels.available_notice ?? "Availability:",
    slotPlaceholder: productLabels.slot_placeholder ?? "Choose your time(s)",
    slotFullNote: productLabels.slot_full_note ?? "Sold out",
    reserveNow: productLabels.reserve_now ?? "Reserve now",
    reserveSeat: productLabels.reserve_seat ?? "Reserve selected times",
    errorGeneric: productLabels.error_generic ?? "Could not add to cart",
    eventDateLabel: productLabels.event_date_label ?? "Date",
    eventTimeLabel: productLabels.event_time_label ?? "Time",
    eventLocationLabel: productLabels.event_location_label ?? "Location",
    descriptionHeading: productLabels.description_heading ?? "Description",
    schedulesLabel: productLabels.schedules_label ?? "Schedules",
    soldIndividuallyNote: productLabels.sold_individually_note ?? "Limit of 1 ticket per person",
    checkingAvailability: productLabels.checking_availability ?? "Checking current availability…",
    unlimitedLabel: productLabels.unlimited_label ?? "Unlimited",
    availableSingular: productLabels.available_singular ?? "{n} available",
    availablePlural: productLabels.available_plural ?? "{n} available",
    ticketsRemainingSingular: productLabels.tickets_remaining_singular ?? "{n} ticket remaining",
    ticketsRemainingPlural: productLabels.tickets_remaining_plural ?? "{n} tickets remaining",
    alreadyInCart: productLabels.already_in_cart ?? "Already in cart",
    descriptionComingSoon: productLabels.description_coming_soon ?? "Description will appear here soon.",
    detailsNotAvailable: productLabels.details_not_available ?? "Event details are not available yet.",
    eventInfoComingSoon: productLabels.event_info_coming_soon ?? "Event info will appear here soon.",
    selectAtLeastOne: productLabels.select_at_least_one ?? "Please select at least one time.",
    missingProductId: productLabels.missing_product_id ?? "Missing product ID.",
    missingBasketRoute: productLabels.missing_basket_route ?? "Missing basket page route.",
    addToCartFailed: productLabels.add_to_cart_failed ?? "Failed to add to cart.",
    decreaseQtyAria: productLabels.decrease_qty_aria ?? "Decrease quantity",
    increaseQtyAria: productLabels.increase_qty_aria ?? "Increase quantity",
    decreaseSlotQtyAria: productLabels.decrease_slot_qty_aria ?? "Decrease slot quantity",
    increaseSlotQtyAria: productLabels.increase_slot_qty_aria ?? "Increase slot quantity",
  };

  const product = useMemo<ProductData>(() => {
    if (isProductData(data)) return data;
    return {};
  }, [data]);

  const locale = useMemo(() => resolveLocale(lang), [lang]);

  const soldIndividually = useMemo(
    () => product.sold_individually === true,
    [product]
  );

  const title = useMemo(() => getRenderedTitle(product, L.productFallback), [product, L.productFallback]);
  const hero = useMemo(() => pickHeroImage(product, L.productFallback), [product, L.productFallback]);
  const descriptionHtml = useMemo(() => getDescriptionHtml(product), [product]);
  const eventDetailsHtml = useMemo(() => getEventDetailsHtml(product), [product]);
  const foo = product.fooevents ?? null;
  const eventType = foo?.WooCommerceEventsType ?? null;
  const startTime = formatTime(foo?.WooCommerceEventsHour, foo?.WooCommerceEventsMinutes);
  const endTime = formatTime(foo?.WooCommerceEventsHourEnd, foo?.WooCommerceEventsMinutesEnd);
  const location = foo?.WooCommerceEventsLocation ?? null;

  const eventKind = useMemo(() => getEventKind(product), [product]);

  const ticketRequired = useMemo(
    () => !!product.acf?.ticket_required,
    [product.acf?.ticket_required]
  );

  const externalBookingUrl = useMemo(
    () => getExternalBookingUrl(product.acf),
    [product.acf]
  );

  const isPaid = useMemo(() => {
    const amount = typeof product.price_amount === "number" ? product.price_amount : NaN;
    return Number.isFinite(amount) && amount > 0;
  }, [product.price_amount]);

  const esPaid = isEsSite && isPaid;
  const useExternalBooking = isEsSite && !!externalBookingUrl;
  const ticketFlowEnabled = ticketRequired && !esPaid && !useExternalBooking;

  const homeLabel = safePageLabel(layout.company_data?.home_page, L.homeFallback);
  const homeHref = safePageHref(layout.company_data?.home_page);
  const shopLabel = safePageLabel(layout.company_data?.shop_page, L.homeFallback);
  const shopHref = safePageHref(layout.company_data?.shop_page);

  const [tab, setTab] = useState<TabKey>("description");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [liveStockBySlotId, setLiveStockBySlotId] = useState<Record<string, number | null>>({});

  const busy = isAdding || redirecting;

  const productId = useMemo(() => {
    const id = Number(product.id);
    return Number.isFinite(id) ? id : null;
  }, [product.id]);

  const alreadyInCart = useMemo(() => {
    if (!productId) return false;
    return cartItems.some((it) => {
      const raw = it.id;
      const n = Number(raw);
      return Number.isFinite(n) && n === productId;
    });
  }, [cartItems, productId]);

  const slots = useMemo(() => product.fooevents_slots?.slots ?? [], [product.fooevents_slots?.slots]);

  const hasSlots = useMemo(
    () => Array.isArray(slots) && slots.length > 0 && eventType !== "single",
    [slots, eventType]
  );

  const isPastEvent = useMemo(() => {
    const hasEventDate = !!(
      foo?.WooCommerceEventsDate ||
      foo?.WooCommerceEventsDateMySQLFormat ||
      (Array.isArray(slots) && slots.length > 0)
    );
    if (!hasEventDate) return false;
    return !isUpcoming(product);
  }, [product, foo, slots]);

  const remainingSlotStockById = useMemo(() => {
    const map: Record<string, number | null> = {};
    for (const s of slots) {
      const slotId = safeStr(s.id);
      const bookingDateId = safeStr(s.booking_date_id);
      if (!slotId) continue;
      const usingLiveStock = Object.prototype.hasOwnProperty.call(
        liveStockBySlotId,
        slotId
      );

      const rawStock = usingLiveStock
        ? liveStockBySlotId[slotId]
        : safeSlotStock(s.stock);

      if (rawStock === null) { map[slotId] = null; continue; }

      const alreadyInCartQty =
        !usingLiveStock && bookingDateId
          ? getQtyInCartForSlot(cartItems, slotId, bookingDateId)
          : 0;

      map[slotId] = Math.max(0, rawStock - alreadyInCartQty);
    }
    return map;
  }, [slots, cartItems, liveStockBySlotId]);

  const statusLabel = useMemo(
    () => getStatusLabel(
      product,
      { free: L.free, endedLabel: L.endedLabel, outOfStockLabel: L.outOfStockLabel },
      remainingSlotStockById,
      isPastEvent
    ),
    [product, remainingSlotStockById, isPastEvent, L.free, L.endedLabel, L.outOfStockLabel]
  );

  const isSoldOut = useMemo(() => {
    if (isPastEvent) return false;
    return hasSlots
      ? !hasAvailableSlots(slots, remainingSlotStockById)
      : !hasAvailableNonSlotTickets(product);
  }, [isPastEvent, hasSlots, slots, remainingSlotStockById, product]);

  const lowAvailability = useMemo(() => {
    if (hasSlots) return null;
    if (isSoldOut) return null;
    if (isPastEvent) return null;
    const direct = product.tickets_left;
    if (typeof direct === "number" && Number.isFinite(direct)) {
      const n = Math.max(0, Math.floor(direct));
      if (n < 4) {
        const template = n === 1 ? L.ticketsRemainingSingular : L.ticketsRemainingPlural;
        return interpolate(template, { n });
      }
    }
    return null;
  }, [hasSlots, isSoldOut, isPastEvent, product, L.ticketsRemainingSingular, L.ticketsRemainingPlural]);

  const slotDates = useMemo(() => {
    if (!hasSlots) return [];
    return getUniqueSortedSlotDates(slots);
  }, [hasSlots, slots]);

  const displayEventDate = useMemo<string | null>(() => {
    if (hasSlots) {
      if (slotDates.length === 0) return null;
      const first = parseDateOnlyLocal(slotDates[0]);
      const last = parseDateOnlyLocal(slotDates[slotDates.length - 1]);
      if (!first) return null;
      const fmt = new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      if (!last || first.getTime() === last.getTime()) return fmt.format(first);
      return `${fmt.format(first)} – ${fmt.format(last)}`;
    }
    const label = getEventDateLabel(
      product,
      locale,
      { weekday: "long", day: "numeric", month: "long", year: "numeric" },
    );
    return label || null;
  }, [hasSlots, slotDates, locale, product]);

  const selectDayRows = useMemo(() => {
    if (eventKind !== "select") return [];
    return getEventDayRows(product);
  }, [eventKind, product]);

  const showFixedEventTime =
    !hasSlots && eventKind !== "select" && !!(startTime || endTime);

  const hasMeta = !!(
    displayEventDate ||
    showFixedEventTime ||
    location ||
    selectDayRows.length > 0
  );

  const groupedSlots = useMemo(() => {
    if (!hasSlots) return [];
    return groupSlotsByDate(slots, L.dateFallback);
  }, [hasSlots, slots, L.dateFallback]);

  async function refreshLiveAvailability() {
    if (!productId) return;
    if (isPastEvent) return;

    try {
      const res = await fetch(apiPath(`/api/events/${productId}/availability`), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(lang ? { "X-Lang": lang } : {}),
        },
        cache: "no-store",
      });

      if (!res.ok) return;

      const json = (await res.json().catch(() => null)) as {
        slots?: Array<{ id?: unknown; stock?: unknown }>;
      } | null;

      const rows = Array.isArray(json?.slots) ? json.slots : [];
      const next: Record<string, number | null> = {};

      for (const s of rows) {
        const id = safeStr(s.id);
        if (!id) continue;

        next[id] =
          s.stock === null || s.stock === undefined
            ? null
            : safeSlotStock(s.stock);
      }

      setLiveStockBySlotId(next);
    } catch {
      // Keep the build-time stock as fallback if live availability fails.
    }
  }

  useEffect(() => {
    void refreshLiveAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, lang, isPastEvent]);

  const [slotQty, setSlotQty] = useState<Record<string, number>>({});

  useEffect(() => {
    if (isPastEvent) { setCartLoaded(true); return; }
    let cancelled = false;
    async function loadCartForAvailability() {
      try {
        const res = await fetch(apiPath("/api/wc/cart/get"), {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(lang ? { "X-Lang": lang } : {}),
          },
          cache: "no-store",
          credentials: "include",
        });
        if (!res.ok) { if (!cancelled) setCartLoaded(true); return; }
        const json = (await res.json().catch(() => null)) as CartResponse | null;
        const nextItems = Array.isArray(json?.items) ? json.items : [];
        if (!cancelled) { setCartItems(nextItems); setCartLoaded(true); }
      } catch {
        if (!cancelled) setCartLoaded(true);
      }
    }
    void loadCartForAvailability();
    return () => { cancelled = true; };
  }, [lang, isPastEvent]);

  function changeSlotQty(slotId: string, next: number, max: number | null) {
    const effectiveMax = soldIndividually
      ? (max === null ? 1 : Math.min(1, max))
      : max;

    const capped =
      effectiveMax === null
        ? Math.max(0, Math.floor(next))
        : clampInt(next, 0, effectiveMax);

    if (soldIndividually && capped > 0) {
      setSlotQty({ [slotId]: 1 });
      return;
    }

    setSlotQty((prev) => ({ ...prev, [slotId]: capped }));
  }

  const selectedBookings = useMemo(() => {
    if (!hasSlots) return [];
    const out: Array<{
      slot_id: string;
      booking_date_id: string;
      label?: string;
      date?: string;
      time?: string;
      datetime?: string;
      quantity: number;
    }> = [];
    for (const s of slots) {
      const slot_id = safeStr(s.id);
      if (!slot_id) continue;
      const q = Number(slotQty[slot_id] ?? 0);
      if (!Number.isFinite(q) || q <= 0) continue;
      const booking_date_id = safeStr(s.booking_date_id);
      if (!booking_date_id) return [];
      out.push({
        slot_id,
        booking_date_id,
        label: safeStr(s.label) || undefined,
        date: safeStr(s.date) || undefined,
        time: safeStr(s.time) || undefined,
        datetime: safeStr(s.datetime) || undefined,
        quantity: Math.floor(q),
      });
    }
    return out;
  }, [hasSlots, slots, slotQty]);

  const totalSelected = useMemo(
    () => selectedBookings.reduce((sum, b) => sum + (b.quantity || 0), 0),
    [selectedBookings]
  );

  const [qty, setQty] = useState<number>(1);

  useEffect(() => {
    if (soldIndividually) setQty(1);
  }, [soldIndividually]);

  async function addToCartAndRedirect() {
    setAddError(null);
    if (!productId) { setAddError(L.missingProductId); return; }
    if (!basketHref) { setAddError(L.missingBasketRoute); return; }
    if (!ticketFlowEnabled || isSoldOut || isPastEvent) return;
    if (soldIndividually && alreadyInCart && !hasSlots) return;
    setIsAdding(true);
    try {
      if (hasSlots && selectedBookings.length === 0) {
        setAddError(L.selectAtLeastOne);
        setIsAdding(false);
        return;
      }
      const payload = hasSlots
        ? { id: productId, bookings: selectedBookings }
        : { id: productId, quantity: soldIndividually ? 1 : qty };
      const res = await fetch(apiPath("/api/wc/cart/add"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(lang ? { "X-Lang": lang } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await getNiceErrorMessage(res);
        await refreshLiveAvailability();
        setSlotQty({});
        setAddError(msg);
        setIsAdding(false);
        return;
      }
      const cartUnknown: unknown = await res.json();
      const cart = cartUnknown as { items_count?: unknown };
      if (cart.items_count !== undefined && cart.items_count !== null) {
        const n = Number(cart.items_count);
        const safe = Number.isFinite(n) && n >= 0 ? n : 0;
        setItemsCount(safe);
      } else {
        await refreshCart();
      }
      setRedirecting(true);
      window.setTimeout(() => { setIsAdding(false); setRedirecting(false); }, 8000);
      router.push(basketHref);
    } catch (e) {
      const msg = e instanceof Error ? e.message : L.addToCartFailed;
      setAddError(msg);
      setIsAdding(false);
      setRedirecting(false);
    }
  }

  function openExternalBooking() {
    if (!externalBookingUrl) return;
    window.open(externalBookingUrl, "_blank", "noopener,noreferrer");
  }

  const statusClassName = isPastEvent
    ? "font-medium opacity-70"
    : isSoldOut
      ? "font-medium text-fuji-red"
      : "";

  const perDayDateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
    [locale],
  );
  const perDayTimeFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    [locale],
  );

  const renderMetaBlock = () => (
    <div className="grid gap-3 text-body">
      {displayEventDate ? (
        <div>
          <span className="font-medium">{L.eventDateLabel}: </span>
          <span className="opacity-80">{displayEventDate}</span>
        </div>
      ) : null}

      {showFixedEventTime ? (
        <div>
          <span className="font-medium">{L.eventTimeLabel}: </span>
          <span className="opacity-80">
            {startTime ?? "—"}
            {endTime ? ` – ${endTime}` : ""}
          </span>
        </div>
      ) : null}

      {eventKind === "select" && selectDayRows.length > 0 ? (
        <div>
          <span className="font-medium">{L.schedulesLabel}:</span>
          <ul className="mt-1 grid gap-1 opacity-80 pl-0">
            {selectDayRows.map((row, i) => (
              <li key={i}>
                <span className="font-medium">{perDayDateFmt.format(row.date)}: </span>
                {perDayTimeFmt.format(row.start)}
                {row.end ? ` – ${perDayTimeFmt.format(row.end)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {location ? (
        <div>
          <span className="font-medium">{L.eventLocationLabel}: </span>
          <span className="opacity-80">{location}</span>
        </div>
      ) : null}
    </div>
  );

  return (
    <Section>
      <Wrapper>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-fuji-grey">
              {hero.src ? (
                <Image
                  src={hero.src}
                  alt={hero.alt}
                  fill
                  sizes="(min-width: 1024px) 58vw, 100vw"
                  className={`object-cover`}
                  priority={false}
                />
              ) : (
                <div className="absolute inset-0 bg-fuji-grey" />
              )}
            </div>
          </div>

          <div className="lg:col-span-6">
            <nav aria-label="Breadcrumb" className="text-body-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-black/70">
                <Link className="no-underline hover:no-underline" href={homeHref}>{homeLabel}</Link>
                <span aria-hidden="true" className="text-black/40">/</span>
                <Link className="no-underline hover:no-underline" href={shopHref}>{shopLabel}</Link>
                <span aria-hidden="true" className="text-black/40">/</span>
                <span className="text-black/80"><SafeHtml content={title} /></span>
              </div>
            </nav>

            <h1 className="mt-3 text-title-1"><SafeHtml content={title} /></h1>

            <div className="mt-4 mb-5 text-body font-semibold">
              <span className={statusClassName}>{statusLabel}</span>
            </div>

            {lowAvailability ? (
              <div className="mt-2 text-body">
                <span className="opacity-80">{L.availableNotice}</span>{" "}
                <span className="font-medium">{lowAvailability}</span>
              </div>
            ) : null}

            {hasMeta ? renderMetaBlock() : null}

            <div className="mt-6">
              {isPastEvent ? (
                <div className="rounded-xl border border-fuji-grey-dark bg-fuji-grey p-4">
                  <div className="font-medium">{L.endedLabel}</div>
                  <div className="mt-1 text-sm opacity-80">
                    {renderPastNote(L.pastNote, shopHref, shopLabel)}
                  </div>
                </div>
              ) : useExternalBooking ? (
                <Button
                  type="button"
                  className="group"
                  onClick={openExternalBooking}
                >
                  <SafeHtml content={L.reserveNow} />
                </Button>
              ) : esPaid ? null : ticketFlowEnabled ? (
                <div className="grid gap-4">
                  {hasSlots ? (
                    <div className="rounded-2xl border p-5">
                      <div className="text-body opacity-80">{L.slotPlaceholder}</div>
                      {soldIndividually ? (
                        <div className="mt-1 text-sm opacity-70">{L.soldIndividuallyNote}</div>
                      ) : null}
                      {!cartLoaded ? (
                        <div className="mt-4 text-sm opacity-70">{L.checkingAvailability}</div>
                      ) : null}
                      <div className="mt-4 grid gap-6">
                        {groupedSlots.map((g) => (
                          <div key={g.dateKey} className="grid gap-3">
                            <div className="font-semibold text-body">{formatSlotDateLabel(g.dateKey, locale)}</div>
                            <div className="grid gap-2">
                              {g.items.map((s) => {
                                const id = safeStr(s.id);
                                if (!id) return null;
                                const label = formatTimeString24(s.time);
                                const remaining = Object.prototype.hasOwnProperty.call(remainingSlotStockById, id)
                                  ? remainingSlotStockById[id]
                                  : safeSlotStock(s.stock);
                                const max = remaining === null ? null : remaining;
                                const effectiveMax = soldIndividually
                                  ? (max === null ? 1 : Math.min(1, max))
                                  : max;
                                const q = Number(slotQty[id] ?? 0);
                                const qSafe = Number.isFinite(q) ? Math.max(0, Math.floor(q)) : 0;
                                const plusDisabled =
                                  busy ||
                                  max === 0 ||
                                  (effectiveMax !== null && qSafe >= effectiveMax) ||
                                  (soldIndividually && totalSelected >= 1 && qSafe === 0);
                                const availabilityText =
                                  max === null
                                    ? L.unlimitedLabel
                                    : max > 0
                                      ? interpolate(max === 1 ? L.availableSingular : L.availablePlural, { n: max })
                                      : L.slotFullNote;
                                return (
                                  <div
                                    key={id}
                                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3"
                                  >
                                    <div className="min-w-[220px]">
                                      <div className="font-medium text-body">
                                        {label}
                                      </div>
                                      <div className="text-sm opacity-70">
                                        {availabilityText}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center overflow-hidden rounded-lg border">
                                        <button
                                          type="button"
                                          className="border-r px-3 py-2 disabled:opacity-50"
                                          onClick={() => changeSlotQty(id, qSafe - 1, max)}
                                          disabled={busy || qSafe <= 0 || max === 0}
                                          aria-label={L.decreaseSlotQtyAria}
                                        >-</button>
                                        <div className="min-w-[48px] px-4 py-2 text-center tabular-nums">{qSafe}</div>
                                        <button
                                          type="button"
                                          className="border-l px-3 py-2 disabled:opacity-50"
                                          onClick={() => changeSlotQty(id, qSafe + 1, max)}
                                          disabled={plusDisabled}
                                          aria-label={L.increaseSlotQtyAria}
                                        >+</button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 text-body opacity-80">
                        {L.quantityLabel}: <span className="font-medium">{totalSelected}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {!soldIndividually ? (
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="flex items-center overflow-hidden rounded-lg border">
                            <button
                              type="button"
                              className="border-r px-3 py-2 disabled:opacity-50"
                              onClick={() => setQty((q) => Math.max(1, q - 1))}
                              disabled={busy || isSoldOut}
                              aria-label={L.decreaseQtyAria}
                            >-</button>
                            <div className="min-w-[48px] px-4 py-2 text-center tabular-nums">{qty}</div>
                            <button
                              type="button"
                              className="border-l px-3 py-2 disabled:opacity-50"
                              onClick={() => setQty((q) => q + 1)}
                              disabled={busy || isSoldOut}
                              aria-label={L.increaseQtyAria}
                            >+</button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm opacity-70">{L.soldIndividuallyNote}</div>
                      )}
                    </div>
                  )}
                  <Button
                    type="button"
                    className="group"
                    disabled={
                      busy ||
                      isSoldOut ||
                      !productId ||
                      !basketHref ||
                      (soldIndividually && alreadyInCart && !hasSlots) ||
                      (hasSlots && selectedBookings.length === 0) ||
                      (soldIndividually && hasSlots && totalSelected > 1)
                    }
                    loading={busy}
                    onClick={addToCartAndRedirect}
                  >
                    <SafeHtml
                      content={
                        isSoldOut
                          ? L.outOfStockLabel
                          : soldIndividually && alreadyInCart && !hasSlots
                            ? L.alreadyInCart
                            : hasSlots
                              ? L.reserveSeat
                              : L.reserveNow
                      }
                    />
                  </Button>
                </div>
              ) : (
                <div className="rounded-xl border p-4">
                  <div className="font-medium">{L.noTicketRequired}</div>
                  <div className="mt-1 text-sm opacity-80">{L.noReservationNote}</div>
                </div>
              )}
            </div>

            {addError ? (
              <div className="mt-4 overflow-hidden">
                <div className="border-t-4 border-fuji-red bg-fuji-grey px-5 py-4">
                  <div className="font-bold">{L.errorGeneric}</div>
                  <div className="mt-1 text-sm opacity-80">{addError}</div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-12">
          <div className="relative">
            <div className="absolute inset-x-0 top-0 border-t" />
            <div className="flex items-start gap-8">
              <button
                type="button"
                onClick={() => setTab("description")}
                className={[
                  "border-t-4 pt-3 pb-3 font-bold transition-opacity",
                  tab === "description" ? "border-fuji-green opacity-100" : "border-transparent opacity-70",
                ].join(" ")}
                aria-current={tab === "description" ? "page" : undefined}
              >
                {L.descriptionHeading}
              </button>
              <button
                type="button"
                onClick={() => setTab("details")}
                className={[
                  "border-t-4 pt-3 pb-3 font-bold transition-opacity",
                  tab === "details" ? "border-fuji-green opacity-100" : "border-transparent opacity-70",
                ].join(" ")}
                aria-current={tab === "details" ? "page" : undefined}
              >
                {L.detailsTab}
              </button>
            </div> 
          </div>

          <div className="pt-8">
            {tab === "description" ? (
              descriptionHtml ? (
                <div className="max-w-none"><SafeHtml content={descriptionHtml} flow variant="product" /></div>
              ) : (
                <div className="rounded-xl border p-6 text-sm opacity-80">
                  {L.descriptionComingSoon}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                  {hasMeta ? renderMetaBlock() : (
                    <div className="mt-3 text-body opacity-80">
                      {L.detailsNotAvailable}
                    </div>
                  )}
                </div>
                <div className="lg:col-span-12">
                  {eventDetailsHtml ? (
                    <div className="max-w-none"><SafeHtml content={eventDetailsHtml} flow variant="product" /></div>
                  ) : (
                    <div className="text-sm opacity-80">{L.eventInfoComingSoon}</div>
                  )}
                </div>
              </div>
            )}
          </div> 
        </div>
      </Wrapper>
    </Section>
  );
}